<template>
  <KeepAlive>
    <LoadingPage
      v-if="!documentsProcessed || !parties || !documents || isFetchingEnvelope"
    >
      <template
        v-if="!documentsProcessed"
        v-slot:title
      >
        {{ $tc('ui.envelopes.create.steps.parties.processing', numberOfDocuments, {processedCount: numberOfDocumentsProcessed, totalCount: numberOfDocuments}) }}
      </template>
      <template
        v-else
        v-slot:title
      >
        {{ $t('ui.envelopes.create.steps.parties.title') }}
      </template>

      <template v-slot:default>
        <ul class="pl-0">
          <li
            v-for="document in documents"
            :key="document.id"
            class="flex mb-sm"
          >
            <SuccessIndicator
              v-if="document.processed_at || document.is_template"
              size="small"
            />
            <SpinLoader
              v-else
              class="transition-opacity ease-in-out delay-200 duration-150"
              size="extraSmall"
            />
            <span class="ml-xs">{{ document.title }}</span>
          </li>
        </ul>
      </template>
    </LoadingPage>

    <PartiesScreen
      v-else
      :currentParties="parties"
      :documents="documents"
      :parallelSigning="isParallelSigning"
    />
  </KeepAlive>
</template>

<script setup>
// @ts-check

import { computed, onActivated, onDeactivated, ref, watchEffect } from 'vue';
import { useRoute } from 'vue-router/composables';
import { useTimeoutPoll } from '@vueuse/core';

import {
  useEnvelope,
  useEnvelopeDocuments,
  useEnvelopeParties,
} from '@/composables/envelopes';

import LoadingPage from '@/components/library/loaders/loadingPage/LoadingPage/LoadingPage.vue';
import PartiesScreen from '@/components/screens/envelopes/PartiesScreen';
import SuccessIndicator from '@/components/library/indicators/SuccessIndicator.vue';
import SpinLoader from '@/components/library/loaders/SpinLoader.vue';

const route = useRoute();

const { isParallelSigning, isFetchingEnvelope } = useEnvelope(route.params.fingerprint);
const { parties } = useEnvelopeParties(route.params.fingerprint);
const { documents, isFetchingDocuments, refetchDocuments } =
  useEnvelopeDocuments(route.params.fingerprint);

const numberOfDocuments = computed(() => documents.value?.length);

const documentsProcessed = computed(() =>
  !isFetchingDocuments.value &&
  documents.value?.every(document => document.is_template || document.processed_at !== null));

const numberOfDocumentsProcessed = computed(() => documents.value?.filter(document => document.is_template || document.processed_at !== null).length);

const polled = ref(0);

// Backoff polling
const timeout = computed(() => {
  if (polled.value < 5) {
    return 1000;
  } else if (polled.value < 10) {
    return 5000;
  }
  return 10000;
});

async function refetch() {
  if (isFetchingDocuments.value) {
    return;
  }

  if (polled.value < 10) {
    polled.value += 1;
  }

  await refetchDocuments();
}

const { pause, resume } = useTimeoutPoll(refetch, timeout);

watchEffect(() => {
  if (documentsProcessed.value) {
    pause();
  }
});

onActivated(() => {
  polled.value = 0;
  resume();
});

onDeactivated(() => {
  pause();
});
</script>
